import { useEffect, useMemo, useRef, useState } from 'react'
import { INTERACTIVE_KINDS, defaultSpecFor } from '@aurora/core'
import type { InteractiveKind, SkillId } from '@aurora/core'
import { InteractiveRenderer } from '@aurora/interactives'
import { auroraStore, useAuroraStore } from '../../store/auroraStore'
import { activeChild, draftMaterial, interests, materialStatus } from '../../store/selectors'
import { kindLabel } from '../../lib/specs'
import { useResolvedTheme } from '../../hooks/useTheme'
import { KindMark } from './KindMark'
import { SpecPreview } from './SpecPreview'

/**
 * Making something.
 *
 * Describing it wins over picking from a list — "a counting game about the
 * diggers on our street" carries the theme, the level and the reason all at
 * once, and themes are what hold her attention. The thirteen types are here
 * anyway, as a way in for a parent who does not know what to ask for: tapping
 * one writes an opening line into the same box, which stays editable.
 *
 * Generation genuinely takes a few seconds, so it is never dressed up as
 * instant. There is no fake progress bar: the elapsed time is real, the stages
 * are the real stages, and the work belongs to the material rather than to
 * this sheet — closing the sheet does not cancel anything, and the finished
 * activity is in the Studio whenever the parent comes back.
 */

const OPENERS: Partial<Record<string, string>> = {
  tangram: 'A tangram puzzle she can drag and rotate',
  matrix_reasoning: 'A 3 by 3 pattern grid, one square missing',
  sight_word_deck: 'Sight-word cards from the words she still trips on',
  counting_game: 'A counting game about ',
  pattern_builder: 'A pattern to continue, AB then AAB',
  deduction_puzzle: 'A who-took-it mystery with three clues',
  sorting_game: 'A hunt for the one that is small and blue',
  sequence_order: 'Put the steps of ',
  letter_tracing: 'Letter tracing for the letters in her name',
  reading_timer: 'Beat-your-time reading with an easy passage',
  memory_pairs: 'A memory game with ',
  model_3d: 'A 3D model of why it gets dark at night',
  story_builder: 'A story she builds herself about ',
}

/** What actually happens, in order, while a material is being made. */
const STAGES = [
  'Reading where she is right now',
  'Choosing a shape that fits',
  'Writing the puzzle itself',
  'Checking every answer works',
]

export function CreateSheet({ onClose }: { onClose: () => void }) {
  const child = useAuroraStore(activeChild)
  const draft = useAuroraStore(draftMaterial)
  const topics = useAuroraStore(interests)
  const theme = useResolvedTheme()
  const [prompt, setPrompt] = useState('')
  const [kind, setKind] = useState<string | null>(null)
  const [elapsed, setElapsed] = useState(0)
  const input = useRef<HTMLTextAreaElement>(null)

  /** What she is into right now, so a sample already looks like her world. */
  const topic = (topics[0] as any)?.statement as string | undefined

  /**
   * A stock version of the chosen type, built locally and instantly.
   *
   * It is a picture of the format, not the thing being made — "pattern
   * builder" means nothing until you have seen one. The real material is still
   * generated against her rubric and her interests, which takes the few
   * seconds it takes.
   */
  const sample = useMemo(() => {
    if (!kind) return null
    try {
      return defaultSpecFor(kind as InteractiveKind, 3, {
        theme: topic,
        targetSkillIds: ['sample' as SkillId],
      })
    } catch {
      return null
    }
  }, [kind, topic])

  const status = draft ? materialStatus(draft) : undefined
  const busy = status === 'generating'

  useEffect(() => {
    if (!draft) input.current?.focus()
  }, [draft])

  // Real seconds, counted from when the request was made — so it survives a
  // reload and keeps counting from the truth rather than from this mount.
  useEffect(() => {
    if (!busy) {
      setElapsed(0)
      return
    }
    const from = draft?.generation?.requestedAt ?? Date.now()
    const tick = () => setElapsed(Math.floor((Date.now() - from) / 1000))
    tick()
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [busy, draft?.generation?.requestedAt])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const act = () => auroraStore.getState() as any

  const submit = () => {
    const text = prompt.trim()
    if (!child || (!text && !kind)) return
    act().generateMaterial({
      childId: child._id,
      prompt: text || `Make a ${kindLabel(kind ?? undefined).toLowerCase()} for ${child.name}`,
      kind: kind ?? undefined,
    })
  }

  const startOver = () => {
    if (draft) act().deleteArtifact(draft._id)
    setPrompt('')
    setKind(null)
  }

  return (
    <div className="sheet-scrim" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-label={draft ? 'Your new activity' : 'Make something'}
      >
        <header className="sheet-head">
          <h2 className="sheet-title">
            {status === 'ready'
              ? 'Here it is'
              : busy
                ? 'Making it'
                : status === 'failed'
                  ? 'That did not come back'
                  : 'Make something'}
          </h2>
          <button className="sheet-close" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </header>

        <div className="sheet-body">
          {!draft && (
            <>
              <label className="sheet-label" htmlFor="create-prompt">
                Describe it in your own words
              </label>
              <textarea
                id="create-prompt"
                ref={input}
                className="sheet-input"
                rows={3}
                value={prompt}
                placeholder={
                  child
                    ? `Something for ${child.name} — say what it should be about`
                    : 'Say what it should be about'
                }
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit()
                }}
              />

              <p className="sheet-or">or start from a type</p>
              <div className="kind-grid" role="group" aria-label="Activity types">
                {INTERACTIVE_KINDS.map((k) => (
                  <button
                    key={k}
                    className={`kind-tile${kind === k ? ' is-active' : ''}`}
                    aria-pressed={kind === k}
                    onClick={() => {
                      const next = kind === k ? null : k
                      setKind(next)
                      if (next && !prompt.trim()) setPrompt(OPENERS[next] ?? kindLabel(next))
                      input.current?.focus()
                    }}
                  >
                    <span className="kind-tile-mark" aria-hidden="true">
                      <KindMark kind={k} />
                    </span>
                    <span className="kind-tile-label">{kindLabel(k)}</span>
                  </button>
                ))}
              </div>

              {sample && (
                <figure className="sample">
                  <figcaption className="sample-cap">
                    A stock {kindLabel(kind ?? undefined).toLowerCase()}, so you can see the
                    shape of it. Hers will be built around what she is working on.
                  </figcaption>
                  <div className="sample-stage">
                    <InteractiveRenderer spec={sample} theme={theme} onResult={() => {}} />
                  </div>
                </figure>
              )}
            </>
          )}

          {busy && <Progress elapsed={elapsed} prompt={draft?.generation?.prompt} />}

          {status === 'failed' && (
            <div className="sheet-failed">
              <p className="sheet-failed-body">
                {draft?.generation?.error ??
                  'Aurora could not build a version of this that passes its own checks, so nothing was saved.'}
              </p>
              <p className="sheet-failed-hint">
                Describing it a little differently — or naming what she likes right now — usually
                gets a clean one.
              </p>
            </div>
          )}

          {status === 'ready' && <SpecPreview artifact={draft} />}
          {status === 'unplayable' && (
            <div className="sheet-failed">
              <p className="sheet-failed-body">
                What came back was not a runnable activity, so it stays out of Kid Mode.
              </p>
            </div>
          )}
        </div>

        <footer className="sheet-foot">
          {!draft && (
            <>
              <button className="btn btn-quiet" onClick={onClose}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                disabled={!child || (!prompt.trim() && !kind)}
                onClick={submit}
              >
                Make it
              </button>
            </>
          )}

          {busy && (
            <>
              <p className="sheet-foot-note">You can close this — it keeps going.</p>
              <button className="btn btn-ghost" onClick={onClose}>
                Close
              </button>
            </>
          )}

          {(status === 'failed' || status === 'unplayable') && (
            <>
              <button className="btn btn-quiet" onClick={startOver}>
                Start over
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  const previous = draft?.generation?.prompt ?? ''
                  act().deleteArtifact(draft._id)
                  setPrompt(previous)
                  setKind(draft?.generation?.requestedKind ?? null)
                }}
              >
                Try again
              </button>
            </>
          )}

          {status === 'ready' && (
            <>
              <button className="btn btn-quiet" onClick={startOver}>
                Discard
              </button>
              <button
                className="btn btn-ghost"
                onClick={() => {
                  const previous = draft?.generation?.prompt ?? ''
                  const previousKind = draft?.generation?.requestedKind ?? null
                  act().deleteArtifact(draft._id)
                  setPrompt(previous)
                  setKind(previousKind)
                }}
              >
                Another one
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  act().keepMaterial(draft._id)
                  onClose()
                }}
              >
                Keep it
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  )
}

/**
 * Honest progress.
 *
 * The bar is indeterminate on purpose — a percentage would be invented, and a
 * parent who has watched one crawl to 90% and stop does not believe the next
 * one. What is shown instead is true: the stage it is on and how long it has
 * actually taken.
 */
function Progress({ elapsed, prompt }: { elapsed: number; prompt?: string }) {
  const stage = Math.min(STAGES.length - 1, Math.floor(elapsed / 3))
  const slow = elapsed >= 20

  return (
    <div className="gen" aria-live="polite">
      <div className="gen-bar" aria-hidden="true">
        <span className="gen-bar-sweep" />
      </div>

      {prompt && <p className="gen-prompt">“{prompt}”</p>}

      <ol className="gen-stages">
        {STAGES.map((label, i) => (
          <li
            key={label}
            className={`gen-stage${i < stage ? ' is-done' : i === stage ? ' is-now' : ''}`}
          >
            <span className="gen-stage-dot" aria-hidden="true" />
            {label}
          </li>
        ))}
      </ol>

      <p className="gen-elapsed tnum">
        {elapsed}s{slow ? ' · longer than usual' : ''}
      </p>
      {slow && (
        <p className="gen-slow">
          Still working. Nothing is lost if you close this — it will be in the Studio when it is
          done, even if you shut the app.
        </p>
      )}
    </div>
  )
}

const CloseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
)
